import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  MessageSquare, GitCompare, Bot, BookOpen, FileText,
  LogOut, Plus, Layers, Trash2, Users, Brain, Sparkles, Wand2
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import Logo from "./Logo";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: Layers },
  { to: "/workforce", label: "Workforce", icon: Users, highlight: true },
  { to: "/chat", label: "Chat", icon: MessageSquare },
  { to: "/compare", label: "Compare", icon: GitCompare },
  { to: "/research", label: "Research", icon: BookOpen },
  { to: "/documents", label: "Documents", icon: FileText },
  { to: "/agents", label: "Quick Agents", icon: Bot },
  { to: "/prompts", label: "Prompts", icon: Sparkles },
  { to: "/studio", label: "Model Studio", icon: Wand2, highlight: true },
  { to: "/models", label: "Brains", icon: Brain },
];

export default function Sidebar({ activeConvId, onConvChange }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [convs, setConvs] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/conversations");
      setConvs(data.conversations || []);
    } catch {}
  };

  useEffect(() => { load(); }, [activeConvId, location.pathname, searchParams.toString()]);

  useEffect(() => {
    // Refresh when other tabs/components emit a sidebar refresh event
    const h = () => load();
    window.addEventListener("aimedley:conversations-changed", h);
    const interval = setInterval(load, 15000);
    return () => {
      window.removeEventListener("aimedley:conversations-changed", h);
      clearInterval(interval);
    };
  }, []);

  const del = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await api.delete(`/conversations/${id}`);
      setConvs((c) => c.filter((x) => x.conversation_id !== id));
      if (activeConvId === id && onConvChange) onConvChange(null);
    } catch {}
  };

  return (
    <aside
      className="hidden md:flex flex-col w-72 h-screen sticky top-0 border-r-2 border-slate-900 bg-[#F1F5F9]"
      data-testid="sidebar"
    >
      <div className="p-5 border-b-2 border-slate-900 flex items-center justify-between gap-2">
        <Logo size={36} wordmark="full" tagline />
      </div>

      <nav className="px-3 py-4 space-y-1">
        {NAV.map(({ to, label, icon: Icon, highlight }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl font-heading font-semibold transition-all ${
                isActive
                  ? "bg-slate-900 text-white shadow-brut-sm"
                  : highlight
                    ? "text-slate-900 bg-amber-100 border-2 border-slate-900 hover:bg-amber-200"
                    : "text-slate-700 hover:bg-white hover:border-2 hover:border-slate-900"
              }`
            }
          >
            <Icon size={18} strokeWidth={2.5} />
            <span className="flex-1">{label}</span>
            {highlight && (
              <span className="text-[9px] uppercase tracking-widest bg-slate-900 text-white px-1.5 py-0.5 rounded-full font-bold">
                New
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="px-3 pb-2">
        <button
          data-testid="sidebar-new-chat-btn"
          onClick={() => nav("/chat")}
          className="w-full brut-btn brut-btn-outline"
        >
          <Plus size={16} strokeWidth={2.5} /> New chat
        </button>
      </div>

      <div className="px-4 mt-2 text-[10px] uppercase tracking-widest text-slate-500 font-bold">Recent</div>
      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1" data-testid="conversation-list">
        {convs.length === 0 && (
          <div className="text-xs text-slate-400 px-3 py-2 italic">No conversations yet</div>
        )}
        {convs.map((c) => (
          <NavLink
            key={c.conversation_id}
            to={
              c.kind === "compare" ? `/compare?c=${c.conversation_id}` :
              c.kind === "research" ? `/research?c=${c.conversation_id}` :
              c.kind === "document" ? `/documents?c=${c.conversation_id}` :
              c.kind === "agent" ? `/agents/run?c=${c.conversation_id}` :
              `/chat?c=${c.conversation_id}`
            }
            data-testid={`conv-${c.conversation_id}`}
            className={({ isActive }) =>
              `group flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-white border-2 border-slate-900" : "hover:bg-white"
              }`
            }
          >
            <div className="flex items-center gap-2 truncate">
              <span className="text-xs">
                {c.kind === "compare" ? "⚖️" : c.kind === "agent" ? "🤖" : c.kind === "research" ? "🔬" : c.kind === "document" ? "📄" : "💬"}
              </span>
              <span className="truncate">{c.title || "Untitled"}</span>
            </div>
            <button
              onClick={(e) => del(c.conversation_id, e)}
              data-testid={`delete-conv-${c.conversation_id}`}
              className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500"
            >
              <Trash2 size={14} />
            </button>
          </NavLink>
        ))}
      </div>

      <div className="p-3 border-t-2 border-slate-900 bg-white flex items-center gap-2">
        <div className="w-9 h-9 rounded-full bg-amber-300 border-2 border-slate-900 flex items-center justify-center font-bold font-heading text-slate-900 overflow-hidden">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-full h-full object-cover" />
          ) : (
            (user?.name || user?.email || "?")[0].toUpperCase()
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate" data-testid="sidebar-user-name">{user?.name || "User"}</div>
          <div className="text-xs text-slate-500 truncate">{user?.email}</div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={async () => { await logout(); nav("/login"); }}
          className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
        >
          <LogOut size={16} />
        </button>
      </div>
    </aside>
  );
}
